import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { resolveMediaUrl } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { inputClass, labelClass } from '@/components/ui/inputStyles';
import { defaultMiniproFeatures, type MiniproFeatures } from '@/lib/miniproFeatures';

type SiteItem = { id: number; site_name: string; parent_site_id?: number | null };

type Props = {
  features: MiniproFeatures;
  sites: SiteItem[];
  miniprogramId: number;
  onChange: (next: MiniproFeatures) => void;
  onUpload: (file: File) => Promise<string>;
  /** 嵌入站点配置面板时不包外层 Card */
  embedded?: boolean;
  cardTitle?: string;
  footer?: ReactNode;
  adsManageHref?: string;
};

export function MiniprogramFeaturesCard({
  features,
  sites,
  miniprogramId,
  onChange,
  onUpload,
  embedded = false,
  cardTitle = '小程序运营扩展（首页统计 / 悬浮钮 / 广告 / 拨号）',
  footer,
  adsManageHref,
}: Props) {
  const f = features || defaultMiniproFeatures();

  const patch = (partial: Partial<MiniproFeatures>) => onChange({ ...f, ...partial });

  async function pickQr(
    kind: 'publish' | 'promo',
    e: React.ChangeEvent<HTMLInputElement>,
  ) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || miniprogramId < 1) return;
    const path = await onUpload(file);
    patch({
      float_actions: {
        ...f.float_actions,
        [kind === 'publish' ? 'publish_qr_path' : 'promo_qr_path']: path,
      },
    });
  }

  async function pickPlaceholderQr(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || miniprogramId < 1) return;
    const path = await onUpload(file);
    patch({ home_ads: { ...f.home_ads, placeholder_qr_path: path } });
  }

  const body = (
    <>
      <section className="mb-8 rounded-xl border border-slate-200 bg-slate-50/80 p-4">
        <label className="mb-2 flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-800">
          <input
            type="checkbox"
            checked={f.home_stats.enabled}
            onChange={(e) =>
              patch({ home_stats: { ...f.home_stats, enabled: e.target.checked } })
            }
          />
          首页展示数据统计（幻灯片下、类型栏上）
        </label>
        {f.home_stats.enabled ? (
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {(
              [
                ['virtual_views', '浏览量虚拟增量'],
                ['virtual_entries', '信息量虚拟增量'],
                ['virtual_dials', '拨号量虚拟增量'],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className={labelClass}>
                <span>{label}</span>
                <input
                  type="number"
                  min={0}
                  className={inputClass}
                  value={f.home_stats[key]}
                  onChange={(e) =>
                    patch({
                      home_stats: {
                        ...f.home_stats,
                        [key]: Math.max(0, Number(e.target.value) || 0),
                      },
                    })
                  }
                />
              </label>
            ))}
          </div>
        ) : null}
        <p className="mt-2 text-xs text-slate-500">展示值 = 当前筛选站点下真实统计 + 虚拟增量。</p>
      </section>

      <section className="mb-8 rounded-xl border border-slate-200 bg-slate-50/80 p-4">
        <label className="mb-2 flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-800">
          <input
            type="checkbox"
            checked={f.float_actions.enabled}
            onChange={(e) =>
              patch({ float_actions: { ...f.float_actions, enabled: e.target.checked } })
            }
          />
          全站底部悬浮按钮「我要发布」「我要推广」
        </label>
        {f.float_actions.enabled ? (
          <div className="mt-3 grid gap-4 md:grid-cols-2">
            {(['publish', 'promo'] as const).map((kind) => {
              const path =
                kind === 'publish' ? f.float_actions.publish_qr_path : f.float_actions.promo_qr_path;
              const title = kind === 'publish' ? '我要发布 · 微信二维码' : '我要推广 · 微信二维码';
              return (
                <div key={kind}>
                  <p className="mb-2 text-xs font-medium text-slate-600">{title}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {path ? (
                      <img
                        src={resolveMediaUrl(path)}
                        alt=""
                        className="h-20 w-20 rounded-lg border border-slate-200 object-cover"
                      />
                    ) : null}
                    <label className="cursor-pointer rounded border border-slate-300 px-2 py-1 text-xs hover:bg-white">
                      上传
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => void pickQr(kind, e)}
                      />
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </section>

      <section className="mb-8 rounded-xl border border-slate-200 bg-slate-50/80 p-4">
        <label className="mb-2 flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-800">
          <input
            type="checkbox"
            checked={f.home_ads.enabled}
            onChange={(e) => patch({ home_ads: { ...f.home_ads, enabled: e.target.checked } })}
          />
          首页列表上方横排广告（宽高比 5:1）
        </label>
        {f.home_ads.enabled ? (
          <div className="mt-3 space-y-4">
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-3">
              <p className="mb-2 text-xs font-medium text-slate-700">无广告时占位条</p>
              <label className={`${labelClass} mb-3`}>
                <span>占位展示文案</span>
                <input
                  className={inputClass}
                  value={f.home_ads.placeholder_title}
                  placeholder="虚位以待"
                  maxLength={32}
                  onChange={(e) =>
                    patch({ home_ads: { ...f.home_ads, placeholder_title: e.target.value } })
                  }
                />
              </label>
              <p className="mb-2 text-xs text-slate-500">
                无有效广告时展示上述文案；若已上传二维码，用户点击占位条可弹出该图（引导加客服）。
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {f.home_ads.placeholder_qr_path ? (
                  <img
                    src={resolveMediaUrl(f.home_ads.placeholder_qr_path)}
                    alt=""
                    className="h-20 w-20 rounded-lg border border-slate-200 object-cover"
                  />
                ) : null}
                <label className="cursor-pointer rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50">
                  上传二维码
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => void pickPlaceholderQr(e)} />
                </label>
                {f.home_ads.placeholder_qr_path ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="!text-slate-600"
                    onClick={() => patch({ home_ads: { ...f.home_ads, placeholder_qr_path: '' } })}
                  >
                    清除
                  </Button>
                ) : null}
              </div>
            </div>
            <p className="text-xs text-slate-500">
              广告内容请在
              {adsManageHref ? (
                <Link to={adsManageHref} className="mx-1 font-medium text-teal-700 underline">
                  广告管理
                </Link>
              ) : (
                '「广告管理」'
              )}
              中维护；无有效广告时展示上方配置的占位文案。
            </p>
          </div>
        ) : null}
      </section>

      <section className="mb-8 rounded-xl border border-slate-200 bg-slate-50/80 p-4">
        <label className="mb-2 flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-800">
          <input
            type="checkbox"
            checked={f.dial_bubble.enabled}
            onChange={(e) => patch({ dial_bubble: { ...f.dial_bubble, enabled: e.target.checked } })}
          />
          详情页拨号按钮上方气泡提示
        </label>
        {f.dial_bubble.enabled ? (
          <label className={`${labelClass} mt-3`}>
            <span>气泡文案（变量）</span>
            <input
              className={inputClass}
              value={f.dial_bubble.text_template}
              placeholder="已有 {count} 人联系"
              onChange={(e) =>
                patch({
                  dial_bubble: { ...f.dial_bubble, text_template: e.target.value },
                })
              }
            />
            <span className="mt-1 block text-xs text-slate-500">
              可用 {'{count}'}、{'{dial_count}'}、{'{拨号量}'} 表示当前条目的联系次数。
            </span>
          </label>
        ) : null}
      </section>

      <section className="rounded-xl border border-amber-200/80 bg-amber-50/50 p-4">
        <label className="mb-2 flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-800">
          <input
            type="checkbox"
            checked={f.auto_dial.enabled}
            onChange={(e) => patch({ auto_dial: { ...f.auto_dial, enabled: e.target.checked } })}
          />
          在售信息自动增加拨号量
        </label>
        {f.auto_dial.enabled ? (
          <label className={`${labelClass} mt-2 max-w-xs`}>
            <span>增速档位</span>
            <select
              className={inputClass}
              value={f.auto_dial.tier}
              onChange={(e) =>
                patch({ auto_dial: { ...f.auto_dial, tier: Number(e.target.value) as 1 | 2 | 3 } })
              }
            >
              <option value={1}>慢速（低概率、低增幅）</option>
              <option value={2}>中速（推荐）</option>
              <option value={3}>快速（高概率、高增幅）</option>
            </select>
          </label>
        ) : null}
        <p className="mt-2 text-xs text-slate-500">在用户打开详情页时，对未售出条目按档位随机增加拨号次数（用于气泡展示）。</p>
      </section>

      <section className="mt-8 rounded-xl border border-teal-200/80 bg-teal-50/40 p-4">
        <label className="mb-2 flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-800">
          <input
            type="checkbox"
            checked={f.publisher_ads.enabled}
            onChange={(e) =>
              patch({ publisher_ads: { ...f.publisher_ads, enabled: e.target.checked } })
            }
          />
          微信流量主广告（原生模板 / 插屏 / 激励视频）
        </label>
        {f.publisher_ads.enabled ? (
          <div className="mt-3 space-y-4">
            <p className="text-xs text-slate-500">
              广告位 ID 在微信公众平台「流量主 → 广告管理」创建。封面广告请在流量主后台配置「用户-场景播控」，对超高频老用户减开展示。
            </p>
            <div className="grid gap-3 md:grid-cols-3">
              {(
                [
                  ['custom_unit_id', '原生模板（信息流）'],
                  ['interstitial_unit_id', '插屏广告'],
                  ['rewarded_unit_id', '激励视频'],
                ] as const
              ).map(([key, label]) => (
                <label key={key} className={labelClass}>
                  <span>{label}</span>
                  <input
                    className={inputClass}
                    value={f.publisher_ads[key]}
                    placeholder="adunit-..."
                    onChange={(e) =>
                      patch({
                        publisher_ads: { ...f.publisher_ads, [key]: e.target.value.trim() },
                      })
                    }
                  />
                </label>
              ))}
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={f.publisher_ads.custom_enabled}
                  onChange={(e) =>
                    patch({
                      publisher_ads: { ...f.publisher_ads, custom_enabled: e.target.checked },
                    })
                  }
                />
                启用原生模板广告
              </label>
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={f.publisher_ads.interstitial_enabled}
                  onChange={(e) =>
                    patch({
                      publisher_ads: { ...f.publisher_ads, interstitial_enabled: e.target.checked },
                    })
                  }
                />
                启用插屏
              </label>
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={f.publisher_ads.rewarded_enabled}
                  onChange={(e) =>
                    patch({
                      publisher_ads: { ...f.publisher_ads, rewarded_enabled: e.target.checked },
                    })
                  }
                />
                启用激励视频
              </label>
            </div>
            {f.publisher_ads.custom_enabled ? (
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="mb-2 text-xs font-medium text-slate-700">原生模板展示位置</p>
                <div className="flex flex-col gap-2 text-sm">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={f.publisher_ads.custom_index_feed_enabled}
                      onChange={(e) =>
                        patch({
                          publisher_ads: {
                            ...f.publisher_ads,
                            custom_index_feed_enabled: e.target.checked,
                          },
                        })
                      }
                    />
                    首页列表（每 N 条插入）
                  </label>
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={f.publisher_ads.custom_detail_main_enabled}
                      onChange={(e) =>
                        patch({
                          publisher_ads: {
                            ...f.publisher_ads,
                            custom_detail_main_enabled: e.target.checked,
                          },
                        })
                      }
                    />
                    详情页正文下方（推荐）
                  </label>
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={f.publisher_ads.custom_detail_more_enabled}
                      onChange={(e) =>
                        patch({
                          publisher_ads: {
                            ...f.publisher_ads,
                            custom_detail_more_enabled: e.target.checked,
                          },
                        })
                      }
                    />
                    详情页「更多推荐」列表（每 N 条插入）
                  </label>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  新开通默认仅详情页展示；列表信息流与模板样式差异大时可关闭首页/更多推荐。
                </p>
              </div>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-3">
              <label className={labelClass}>
                <span>列表每 N 条插入（首页 / 更多推荐）</span>
                <input
                  type="number"
                  min={3}
                  max={20}
                  className={inputClass}
                  value={f.publisher_ads.feed_interval}
                  onChange={(e) =>
                    patch({
                      publisher_ads: {
                        ...f.publisher_ads,
                        feed_interval: Math.min(20, Math.max(3, Number(e.target.value) || 5)),
                      },
                    })
                  }
                />
              </label>
              <label className={labelClass}>
                <span>单日打开 &gt; 此次数视为超高频（跳过插屏）</span>
                <input
                  type="number"
                  min={1}
                  max={20}
                  className={inputClass}
                  value={f.publisher_ads.daily_open_cap}
                  onChange={(e) =>
                    patch({
                      publisher_ads: {
                        ...f.publisher_ads,
                        daily_open_cap: Math.min(20, Math.max(1, Number(e.target.value) || 3)),
                      },
                    })
                  }
                />
              </label>
              <label className={labelClass}>
                <span>累计打开 &gt; 此次数视为老用户（跳过插屏）</span>
                <input
                  type="number"
                  min={1}
                  max={9999}
                  className={inputClass}
                  value={f.publisher_ads.total_open_cap}
                  onChange={(e) =>
                    patch({
                      publisher_ads: {
                        ...f.publisher_ads,
                        total_open_cap: Math.min(9999, Math.max(1, Number(e.target.value) || 50)),
                      },
                    })
                  }
                />
              </label>
            </div>
            <label className={labelClass}>
              <span>激励视频入口文案</span>
              <input
                className={inputClass}
                value={f.publisher_ads.rewarded_skip_label}
                maxLength={48}
                onChange={(e) =>
                  patch({
                    publisher_ads: { ...f.publisher_ads, rewarded_skip_label: e.target.value },
                  })
                }
              />
              <span className="mt-1 block text-xs text-slate-500">
                用于插屏关闭后弹窗正文（如：看视频，今天不弹窗）。
              </span>
            </label>
            {f.publisher_ads.rewarded_enabled ? (
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <label className="mb-3 flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-800">
                  <input
                    type="checkbox"
                    checked={f.publisher_ads.rewarded_prompt_after_interstitial}
                    onChange={(e) =>
                      patch({
                        publisher_ads: {
                          ...f.publisher_ads,
                          rewarded_prompt_after_interstitial: e.target.checked,
                        },
                      })
                    }
                  />
                  用户关闭插屏后弹出激励引导（须用户点「去观看」才播放）
                </label>
                {f.publisher_ads.rewarded_prompt_after_interstitial ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className={labelClass}>
                      <span>弹窗标题</span>
                      <input
                        className={inputClass}
                        value={f.publisher_ads.rewarded_prompt_title}
                        maxLength={32}
                        onChange={(e) =>
                          patch({
                            publisher_ads: {
                              ...f.publisher_ads,
                              rewarded_prompt_title: e.target.value,
                            },
                          })
                        }
                      />
                    </label>
                    <label className={labelClass}>
                      <span>确认按钮</span>
                      <input
                        className={inputClass}
                        value={f.publisher_ads.rewarded_prompt_confirm_text}
                        maxLength={12}
                        onChange={(e) =>
                          patch({
                            publisher_ads: {
                              ...f.publisher_ads,
                              rewarded_prompt_confirm_text: e.target.value,
                            },
                          })
                        }
                      />
                    </label>
                    <label className={labelClass}>
                      <span>取消按钮</span>
                      <input
                        className={inputClass}
                        value={f.publisher_ads.rewarded_prompt_cancel_text}
                        maxLength={12}
                        onChange={(e) =>
                          patch({
                            publisher_ads: {
                              ...f.publisher_ads,
                              rewarded_prompt_cancel_text: e.target.value,
                            },
                          })
                        }
                      />
                    </label>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </section>
    </>
  );

  if (embedded) {
    return <div className="space-y-4">{body}</div>;
  }

  return (
    <Card title={cardTitle}>
      {body}
      {footer ? <div className="mt-6 flex justify-end border-t border-slate-100 pt-4">{footer}</div> : null}
    </Card>
  );
}
